<script lang="ts" setup>
import { computed, inject, ref, watch, type PropType } from 'vue';
import type { DataTableItem, DataTableColumn as ColumnModel } from '../../models';
import type { useDataTable } from '../../services';
import DataTableTableCell from './table-view/DataTableTableCell.vue';

const props = defineProps({
  item: {
    type: Object as PropType<DataTableItem<any>>,
    required: true
  },
  i: {
    type: Number,
    required: true
  }
});

const emit = defineEmits(['rowAction']);

const dataTableService = inject<ReturnType<typeof useDataTable>>('dataTableService')!;

const reactiveClass = computed(() => {
  if (!dataTableService.getConfig.value.reactiveClassCondition) return;

  return dataTableService.getConfig.value.reactiveClassCondition(props.item);
});

const onRowClick = (item: DataTableItem) => {
  if (!dataTableService.getConfig.value.clickableItems) return;
  onRowAction('click', item);
};

const onRowMouseEnter = (item: DataTableItem) => {
  if (!dataTableService.getConfig.value.hover) return;
  onRowAction('mouseEnter', item);
};

const onRowMouseLeave = (item: DataTableItem) => {
  if (!dataTableService.getConfig.value.hover) return;
  onRowAction('mouseLeave', item);
};

const onRowAction = (action: string, item: DataTableItem, data?: any) => {
  emit('rowAction', action, item, data);
};

const isColumnHidden = (column: ColumnModel) => {
  if (column.condition !== undefined) {
    return !column.condition();
  }

  return false;
};

const rowClass = computed(() => props.item.class);

const trRef = ref<HTMLTableRowElement | null>(null);

watch(
  () => {
    const scrollCondition = dataTableService.getConfig.value.reactiveScrollCondition;
    return scrollCondition ? scrollCondition(props.item) : false;
  },
  (scroll) => {
    if (scroll && trRef.value) {
      trRef.value.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
    }
  }
);
</script>

<template>
  <tr
    ref="trRef"
    class="data-table-row"
    :class="{
      [String(rowClass)]: rowClass,
      disabled: item.disabled,
      'data-table-row--clickable': dataTableService.getConfig.value.clickableItems,
      'data-table-row--selected': item.selected,
      [String(reactiveClass)]: reactiveClass
    }"
    @click="onRowClick(item)"
    @mouseenter="onRowMouseEnter(item)"
    @mouseleave="onRowMouseLeave(item)">
    <template
      v-for="(column, j) in dataTableService.getColumns.value"
      :key="column.name + i + j">
      <data-table-table-cell
        v-if="column.visible && !isColumnHidden(column)"
        :item="item"
        :index="i"
        :column="column"
        @row-action="onRowAction" />
    </template>
  </tr>
</template>
